import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AiOutlineLoading } from "react-icons/ai";
import { HiOutlineMail } from "react-icons/hi";

import { Button, Input } from "../../ui";
import AuthShell from "./components/AuthShell";
import MessageBanner from "./components/MessageBanner";
import PasswordInput from "./components/PasswordInput";
import dosee_logo from "../../../assets/images/dosee.png";
import { useAdminContext } from "../../context/admin/AdminContext";

const Login: React.FC = () => {
  const { adminLogin, sysAdmin } = useAdminContext();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const respond = (err: boolean, msg: string) => {
    setError(err);
    setSuccess(!err);
    setLoading(false);
    setMessage(msg);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setError(false);

    if (!email) {
      respond(true, "Email address is required");
      return;
    }
    if (!password) {
      respond(true, "Password is required");
      return;
    }

    try {
      const response = await adminLogin({ login: email, password });
      if (!response) {
        throw new Error("No response from server. Please check your connection.");
      }
      const { message: m, status } = response;
      if (status !== 200 && status !== 201) {
        respond(true, m);
        return;
      }
      setLoading(false);
    } catch {
      respond(true, "Something went wrong. Please try again later.");
    }
  };

  if (sysAdmin !== null) {
    return <Navigate replace to="/" />;
  }

  return (
    <AuthShell eyebrow="Sign in">
      <div className="mb-8 flex items-center justify-center lg:hidden">
        <img src={dosee_logo} alt="DoseeAfrica" className="h-10 w-auto" />
      </div>

      <header className="mb-8">
        <h1 className="text-[28px] font-semibold leading-tight tracking-tight text-gray-900">
          Welcome back
        </h1>
        <p className="mt-2 text-[13.5px] text-gray-500">
          Sign in to continue where you left off.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label
            htmlFor="login-email"
            className="mb-1.5 block text-[12.5px] font-medium text-gray-700"
          >
            Email address
          </label>
          <Input
            id="login-email"
            type="email"
            value={email}
            autoComplete="email"
            placeholder="you@company.com"
            leadingIcon={<HiOutlineMail className="h-4 w-4" />}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label
              htmlFor="login-password"
              className="block text-[12.5px] font-medium text-gray-700"
            >
              Password
            </label>
          </div>
          <PasswordInput
            id="login-password"
            value={password}
            autoComplete="current-password"
            placeholder="Enter your password"
            onChange={(e) => {
              setPassword(e.target.value);
              setError(false);
              setSuccess(false);
            }}
          />
          <label className="mt-2.5 inline-flex items-center gap-2 text-[12px] text-gray-600">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-brand focus:ring-brand/25"
            />
            Remember me on this device
          </label>
        </div>

        <MessageBanner
          error={error}
          success={success}
          message={message}
          onDismiss={() => {
            setError(false);
            setSuccess(false);
          }}
        />

        <Button
          type={loading ? "button" : "submit"}
          variant="primary"
          className="w-full"
          disabled={loading}
        >
          {loading ? (
            <AiOutlineLoading className="h-4 w-4 animate-spin" />
          ) : (
            "Sign in"
          )}
        </Button>

        <p className="text-[11px] leading-relaxed text-gray-500">
          By continuing, you agree to our
          <Link to="/terms" className="mx-1 text-gray-700 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-700">
            Terms of Use
          </Link>
          and
          <Link to="/privacy" className="ml-1 text-gray-700 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-700">
            Privacy Policy
          </Link>
          .
        </p>
      </form>

      <div className="mt-8 flex items-center justify-between border-t border-gray-100 pt-5 text-[12px]">
        <Link
          to="/signup"
          className="font-medium text-brand hover:text-[#007a7a]"
        >
          Create a company account →
        </Link>
        <a
          href="mailto:support@doseeafrica.com"
          className="text-gray-500 hover:text-gray-800"
        >
          Need help?
        </a>
      </div>
    </AuthShell>
  );
};

export default Login;
